<template>
  <nav class="adv-menu" ref="menuRef" :class="{
    '--reduce-animations': reduceAnimations,
    '--has-left-arrow': canScrollLeft,
    '--has-right-arrow': canScrollRight,
    '--hidden': isHidden
  }" :style="{ '--adv-menu-gap': `${gap}px` }">
    <q-resize-observer v-if="!isHidden" @resize="updateMenuDebounced" :debounce="150"/>

    <!-- Стрелка влево -->
    <div
        v-if="canScrollLeft"
        class="adv-menu__arrow adv-menu__arrow--left"
        @click="scrollSlider('left')"
    >
      <q-icon :name="ICONS.chevronLeftMat" size="20px"/>
    </div>

    <!-- Слайдер контейнер -->
    <div
        ref="sliderRef"
        class="adv-menu__slider"
        :style="{
          gap: `${gap}px`,
          paddingLeft: paddingX,
          paddingRight: paddingX,
          paddingTop: paddingY,
          paddingBottom: paddingY
        }"
    >
      <!-- Индикатор внутри слайдера -->
      <Indicator
          ref="indicatorComponentRef"
          :visible="indicatorVisible"
          :left="indicatorLeft"
          :width="indicatorWidth"
          :color="indicatorColor"
          mode="slider"
          :animation-duration="effectiveAnimationDuration"
          :timing-function="effectiveTimingFunction"
          :padding-y="paddingY"
          :gpu-acceleration="optimizationsEnabled.gpuAcceleration"
          :will-change="optimizationsEnabled.willChange"
      />

      <MenuItem
          v-for="item in processedItems"
          :key="item.id"
          :ref="(el) => setItemRef(el as HTMLElement, item.id)"
          :item="item"
          :uniform-style="uniformStyle"
          :active-background="activeBackground"
          :active-color="activeColor"
          :active-filled-highlight="activeFilledHighlight"
          :active-outline-highlight="activeOutlineHighlight"
          :pause-animation-on-active="pauseAnimationOnActive"
          :enable-text-color-transition="enableTextColorTransition"
          :text-color-getter="getTextColor"
          @click="onItemClick(item)"
      />
    </div>

    <!-- Стрелка вправо -->
    <div
        v-if="canScrollRight"
        class="adv-menu__arrow adv-menu__arrow--right"
        @click="scrollSlider('right')"
    >
      <q-icon :name="ICONS.chevronRightMat" size="20px"/>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ICONS } from "@/assets/icons";
import { useDebounceFn, useThrottleFn } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, toRef, watch } from "vue";
import { useActiveItemDebounce } from "./composables/useActiveItemDebounce";
import { useActiveItemThrottle } from "./composables/useActiveItemThrottle";
import { useElementMeasurements } from "./composables/useElementMeasurements";
import { useGPUTransform } from "./composables/useGPUTransform";
import { useIndicator } from "./composables/useIndicator";
import { useIndicatorTextColor } from "./composables/useIndicatorTextColor";
import { useMenuStateVerification } from "./composables/useMenuStateVerification";
import { type ProcessedItem, useProcessedItems } from "./composables/useProcessedItems";
import { useRAFScheduler } from "./composables/useRAFScheduler";
import { useSliderScroll } from "./composables/useSliderScroll";
import { useVisibilityManager } from "./composables/useVisibilityManager";
import { useWillChange } from "./composables/useWillChange";
import Indicator from "./Indicator.vue";
import MenuItem from "./Item.vue";

export interface AdvMenuItem {
  id: string | number;
  label?: string;
  icon?: string | null;
  activeIcon?: string | null;
  bgColor?: string | null;
  textColor?: string | null;
  indicatorColor?: string | null;
  mode?: 'outline' | 'filled' | 'flat';
  textBlink?: boolean;
  animation?: 'gradient' | 'pulse' | 'shimmer' | 'breathe' | 'glow';
  hidden?: boolean;
  url?: string | null;
}

export type ActiveBgHighlightEffect = "brightness" | "border" | "shadow" | "scale" | "bold";

// Default timing values (ms)
const DEFAULT_TIMINGS = {
  indicatorUpdate: 100,
  menuResize: 250,
  scrollToActive: 150,
  scrollThrottle: 16,
  scrollFinalCalculations: 200,
  externalUpdateDebounce: 400,
  externalUpdateThrottle: 100,
  animationDuration: 300,
} as const;

interface Props {
  items: AdvMenuItem[];
  modelValue?: string | number | null;
  gap?: number;
  /** Цвет фона активного элемента */
  activeBackground?: string;
  /** Цвет текста активного элемента */
  activeColor?: string;
  /** Отключить кастомное оформление (фон, иконки) из свойств пунктов */
  uniformStyle?: boolean;
  /** Уменьшить или отключить анимации */
  reduceAnimations?: boolean;
  /** Включить динамическое изменение цвета текста под индикатором */
  enableTextColorTransition?: boolean;
  /** Массив эффектов выделения активного элемента в режиме filled */
  activeFilledHighlight?: ActiveBgHighlightEffect[];
  /** Массив эффектов выделения активного элемента в режиме outline */
  activeOutlineHighlight?: ActiveBgHighlightEffect[];
  /** Останавливать анимацию фона когда элемент активен */
  pauseAnimationOnActive?: boolean;
  /** Горизонтальные отступы для слайдера (например: '16px', '1rem') */
  paddingX?: string;
  /** Вертикальные отступы для слайдера (например: '8px', '0.5rem') */
  paddingY?: string;
  /** Настройки оптимизации */
  optimizations?: Partial<{
    enableGPUAcceleration: boolean;
    enableWillChange: boolean;
    enableRAFBatching: boolean;
    enableMeasurementCache: boolean;
    enableVisibilityOptimization: boolean;
  }>;
  /** Компонент скрыт (для SSR оптимизации) */
  isHidden?: boolean;
  /**
   * Режим обработки внешних изменений modelValue
   * - 'debounced': задержка после последнего изменения (по умолчанию)
   * - 'throttled': ограничение частоты обновлений
   */
  externalUpdateMode?: 'debounced' | 'throttled';
  /**
   * Настройки таймингов для debounce/throttle операций (мс)
   */
  timings?: Partial<typeof DEFAULT_TIMINGS>;
  /**
   * Включить автоматическую проверку и исправление состояния меню
   * (позиция индикатора и цвет текста) через MutationObserver.
   * Помогает избежать рассинхронизации на медленных устройствах.
   */
  enableStateVerification?: boolean;
  /**
   * Задержка проверки состояния после последнего изменения (мс)
   */
  stateVerificationDelay?: number;
  /**
   * Включить отладочные логи для state verification
   */
  debugStateVerification?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gap: 10,
  activeBackground: "#1976d2",
  activeColor: "#ffffff",
  uniformStyle: false,
  reduceAnimations: false,
  enableTextColorTransition: false,
  activeFilledHighlight: () => ["brightness", "shadow", "scale", "bold"],
  activeOutlineHighlight: () => ["border", "shadow", "scale", "bold"],
  pauseAnimationOnActive: false,
  paddingX: '0px',
  paddingY: '0px',
  optimizations: () => ({
    enableGPUAcceleration: true,
    enableWillChange: true,
    enableRAFBatching: true,
    enableMeasurementCache: true,
    enableVisibilityOptimization: true,
  }),
  isHidden: false,
  externalUpdateMode: 'debounced',
  timings: () => ({}),
  enableStateVerification: true,
  stateVerificationDelay: 1000,
  debugStateVerification: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string | number | null];
  itemClick: [item: AdvMenuItem];
}>();

// Refs
const menuRef = ref<HTMLElement | null>(null);
const sliderRef = ref<HTMLElement | null>(null);
const itemRefs = ref<Record<string, HTMLElement>>({});

// Внутреннее активное значение
const internalActiveId = ref<string | number | null>(props.modelValue ?? null);

// Initialize measurement cache (skip on server)
const measurementCache = process.env.SERVER ? null : useElementMeasurements();

// Initialize RAF scheduler (skip on server)
const rafScheduler = process.env.SERVER ? null : useRAFScheduler();

// Initialize GPU transform manager (skip on server)
const gpuTransform = process.env.SERVER ? {
  isGPUAvailable: () => false,
  applyTransform: () => {
  },
  setBaseDimensions: () => {
  },
  getTransformState: () => ({baseWidth: 0}),
  reset: () => {
  }
} : useGPUTransform({
  transformOrigin: 'left center',
});

// Initialize will-change manager (skip on server)
const willChangeManager = process.env.SERVER
    ? {
      set: () => {
      },
      remove: () => {
      },
      clearAll: () => {
      },
      getActiveElements: () => [],
      isActive: () => false
    }
    : useWillChange({
      cleanupDelay: 500,
    });

// Initialize visibility manager (skip on server)
const visibilityManager = process.env.SERVER ? {
  isVisible: ref(true), observe: () => {
  }, cleanup: () => {
  }
} : useVisibilityManager({
  rootMargin: '50px',
  threshold: 0,
  onVisibilityChange: (isVisible) => {
    if (!optimizationsEnabled.value.visibilityOptimization) {
      return;
    }

    if (isVisible) {
      if (optimizationsEnabled.value.rafBatching && rafScheduler) {
        rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
        rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
      } else {
        updateSliderArrows(sliderRef);
        updateIndicatorDebounced();
      }
    } else {
      rafScheduler?.cancelAll();
    }
  },
});

// Computed: компонент эффективно скрыт (isHidden prop или не в viewport)
const isEffectivelyHidden = computed(() => {
  if (props.isHidden) {
    return true;
  }

  if (optimizationsEnabled.value.visibilityOptimization) {
    return !visibilityManager.isVisible.value;
  }

  return false;
});

// Effective timings with defaults
const effectiveTimings = computed(() => ({
  indicatorUpdate: props.timings?.indicatorUpdate ?? DEFAULT_TIMINGS.indicatorUpdate,
  menuResize: props.timings?.menuResize ?? DEFAULT_TIMINGS.menuResize,
  scrollToActive: props.timings?.scrollToActive ?? DEFAULT_TIMINGS.scrollToActive,
  scrollThrottle: props.timings?.scrollThrottle ?? DEFAULT_TIMINGS.scrollThrottle,
  scrollFinalCalculations: props.timings?.scrollFinalCalculations ?? DEFAULT_TIMINGS.scrollFinalCalculations,
  externalUpdateDebounce: props.timings?.externalUpdateDebounce ?? DEFAULT_TIMINGS.externalUpdateDebounce,
  externalUpdateThrottle: props.timings?.externalUpdateThrottle ?? DEFAULT_TIMINGS.externalUpdateThrottle,
  animationDuration: props.timings?.animationDuration ?? DEFAULT_TIMINGS.animationDuration,
}));

// Animation duration
const effectiveAnimationDuration = computed(() =>
    props.reduceAnimations ? 0 : effectiveTimings.value.animationDuration
);

// Timing functions
const effectiveTimingFunction = computed(() => 'cubic-bezier(0.4, 0, 0.2, 1)');

// Active item change callback
const onActiveChangeCallback = () => {
  if (optimizationsEnabled.value.rafBatching && rafScheduler) {
    rafScheduler.schedule('scroll-to-active', scrollToActiveItemDebounced, 'normal');
    rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
  } else {
    scrollToActiveItemDebounced();
    updateIndicatorDebounced();
  }
};

const {setActiveDebounced, cancel: cancelActiveDebounce} = useActiveItemDebounce(
    internalActiveId,
    onActiveChangeCallback,
    effectiveTimings.value.externalUpdateDebounce
);

const {setActiveThrottled, cancel: cancelActiveThrottle} = useActiveItemThrottle(
    internalActiveId,
    onActiveChangeCallback,
    effectiveTimings.value.externalUpdateThrottle
);

// Initialize text color transition manager (skip on server)
const textColorManager = process.env.SERVER
    ? {
      getTextColor: () => 'inherit', updateTextColors: () => {
      }, cleanup: () => {
      }
    }
    : (measurementCache && rafScheduler
            ? useIndicatorTextColor(
                toRef(props, 'enableTextColorTransition'),
                toRef(props, 'activeColor'),
                measurementCache,
                rafScheduler
            )
            : {
              getTextColor: () => 'inherit', updateTextColors: () => {
              }, cleanup: () => {
              }
            }
    );

// Optimization configuration
const optimizationsEnabled = ref({
  rafBatching: props.optimizations?.enableRAFBatching ?? true,
  measurementCache: props.optimizations?.enableMeasurementCache ?? true,
  gpuAcceleration: props.optimizations?.enableGPUAcceleration ?? true,
  willChange: props.optimizations?.enableWillChange ?? true,
  visibilityOptimization: props.optimizations?.enableVisibilityOptimization ?? true,
});

// Slider scroll - using composable
const {
  canScrollLeft,
  canScrollRight,
  updateSliderArrows,
  scrollSlider: scrollSliderCore,
  scrollToActiveItem: scrollToActiveItemCore,
  lockScroll,
  invalidateScrollMeasurements,
  cleanup: cleanupScroll
} = useSliderScroll({
  gap: toRef(props, 'gap'),
  measurementCache: measurementCache || null,
  rafScheduler: rafScheduler || null,
  willChangeManager: willChangeManager || null,
  optimizationsEnabled: computed(() => ({
    rafBatching: optimizationsEnabled.value.rafBatching,
    willChange: optimizationsEnabled.value.willChange
  })),
  isEffectivelyHidden
});

// Indicator - using composable
const indicatorComponentRef = ref<InstanceType<typeof Indicator> | null>(null);

const {
  indicatorLeft,
  indicatorWidth,
  indicatorVisible,
  indicatorColor,
  indicatorRef: indicatorComposableRef,
  updateIndicator: updateIndicatorCore,
} = useIndicator({
  mode: ref('slider'),
  activeBackground: toRef(props, 'activeBackground'),
  animationDuration: computed(() => effectiveAnimationDuration.value),
  measurementCache: measurementCache || null,
  rafScheduler: rafScheduler || null,
  gpuTransform: gpuTransform,
  willChangeManager: willChangeManager || null,
  enableGPU: computed(() => optimizationsEnabled.value.gpuAcceleration),
  enableWillChange: computed(() => optimizationsEnabled.value.willChange),
  enableRAF: computed(() => optimizationsEnabled.value.rafBatching),
});

// Sync component ref with composable ref
watch(indicatorComponentRef, (newVal) => {
  if (newVal) {
    indicatorComposableRef.value = newVal;
  }
});

// Processed items - using composable
const {
  processedItems,
  invalidateCache: invalidateProcessedItemsCache
} = useProcessedItems(
    toRef(props, 'items'),
    internalActiveId,
    toRef(props, 'uniformStyle'),
    measurementCache
);

// Methods
const setItemRef = (el: any, _id: string | number) => {
  if (el) {
    // If el is a component instance, get its root element
    const element = el.$el || el;
    if (element && element instanceof HTMLElement) {
      itemRefs.value[String(_id)] = element;
    }
  }
};

// Text color getter for Item component
const getTextColor = (_id: string | number, color: string, isActive: boolean) => {
  return textColorManager?.getTextColor(_id, color, isActive) || color;
};

// Флаг для однократной проверки скролла после монтирования
const scrollVerificationDone = ref(false);

// Общий счетчик попыток проверки при невалидных refs/элементах
let verificationRetryCount = 0;
const MAX_VERIFICATION_RETRIES = 3;
const RETRY_DELAY_BASE = 300; // Базовая задержка в мс

// Отслеживание последнего состояния для предотвращения зацикливания
let lastVerificationState: {
  activeId: string | number | null;
  isPositionCorrect: boolean;
  isColorCorrect: boolean;
  timestamp: number;
} | null = null;

const VERIFICATION_COOLDOWN = 2000; // 2 секунды между повторными синхронизациями для одной и той же проблемы

// Функция для вычисления задержки retry (прогрессивная)
const getRetryDelay = (attemptNumber: number) => attemptNumber * RETRY_DELAY_BASE;

// Функция для планирования повторной проверки с задержкой
const scheduleRetryVerification = () => {
  if (verificationRetryCount >= MAX_VERIFICATION_RETRIES) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Max retries reached, stopping');
    }
    return;
  }

  verificationRetryCount++;
  const delay = getRetryDelay(verificationRetryCount);

  if (props.debugStateVerification) {
    console.log(`[MenuVerification] Scheduling retry #${verificationRetryCount} in ${delay}ms`);
  }

  // Даем время DOM элементам смонтироваться с прогрессивной задержкой
  setTimeout(() => {
    if (props.debugStateVerification) {
      console.log(`[MenuVerification] Running retry #${verificationRetryCount} verification`);
    }
    verifyIndicatorState();
  }, delay);
};

// Функция проверки видимости активного пункта в зоне скролла
const verifyActiveItemVisibility = () => {
  const activeId = internalActiveId.value;

  if (!activeId || !sliderRef.value) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Skipping scroll check - missing refs');
    }
    return;
  }

  const activeElement = itemRefs.value[String(activeId)];
  if (!activeElement) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Skipping scroll check - active element not found');
    }
    return;
  }

  const slider = sliderRef.value;
  const sliderRect = slider.getBoundingClientRect();
  const itemRect = activeElement.getBoundingClientRect();

  // Проверяем находится ли активный элемент в видимой области слайдера
  const isFullyVisible =
    itemRect.left >= sliderRect.left &&
    itemRect.right <= sliderRect.right;

  const isPartiallyVisible =
    itemRect.right > sliderRect.left &&
    itemRect.left < sliderRect.right;

  if (props.debugStateVerification) {
    console.log('[MenuVerification] Scroll visibility check', {
      activeId,
      sliderRect: { left: sliderRect.left, right: sliderRect.right, width: sliderRect.width },
      itemRect: { left: itemRect.left, right: itemRect.right, width: itemRect.width },
      isFullyVisible,
      isPartiallyVisible
    });
  }

  // Если элемент не полностью видим - прокручиваем к нему
  if (!isFullyVisible) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Active item not fully visible, scrolling to it');
    }

    scrollToActiveItem();
  } else if (props.debugStateVerification) {
    console.log('[MenuVerification] ✅ Active item is fully visible');
  }
};

// Функция проверки состояния индикатора и цвета текста
const verifyIndicatorState = () => {
  const activeId = internalActiveId.value;

  if (props.debugStateVerification) {
    console.log('[MenuVerification] Starting verification', { activeId, retryCount: verificationRetryCount });
  }

  // Проверка 1: Все необходимые refs должны быть готовы
  if (!activeId || !sliderRef.value || !indicatorComponentRef.value) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Skipping verification - missing refs', {
        hasActiveId: !!activeId,
        hasSliderRef: !!sliderRef.value,
        hasIndicatorRef: !!indicatorComponentRef.value,
        retryCount: verificationRetryCount
      });
    }

    // Планируем повторную проверку с задержкой
    scheduleRetryVerification();
    return;
  }

  const activeElement = itemRefs.value[String(activeId)];
  if (!activeElement) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Skipping verification - active element not found', { activeId });
    }
    return;
  }

  // Проверка 2: DOM элемент индикатора должен быть валидным
  const indicatorEl = indicatorComponentRef.value.$el;
  if (!indicatorEl || !(indicatorEl instanceof HTMLElement)) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Skipping verification - indicator element invalid', {
        hasIndicatorEl: !!indicatorEl,
        isHTMLElement: indicatorEl instanceof HTMLElement,
        retryCount: verificationRetryCount
      });
    }

    // Планируем повторную проверку с задержкой
    scheduleRetryVerification();
    return;
  }

  // Все refs и элементы готовы - сбрасываем счетчик
  if (verificationRetryCount > 0) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] All refs and elements are now valid, resetting retry count');
    }
    verificationRetryCount = 0;
  }

  // Проверяем позицию индикатора
  const itemRect = activeElement.getBoundingClientRect();
  const indicatorRect = indicatorEl.getBoundingClientRect();

  const tolerance = 5;

  const isPositionCorrect =
    Math.abs(itemRect.left - indicatorRect.left) < tolerance &&
    Math.abs(itemRect.width - indicatorRect.width) < tolerance;

  if (props.debugStateVerification) {
    console.log('[MenuVerification] Position check', {
      itemRect: { left: itemRect.left, width: itemRect.width },
      indicatorRect: { left: indicatorRect.left, width: indicatorRect.width },
      leftDiff: Math.abs(itemRect.left - indicatorRect.left),
      widthDiff: Math.abs(itemRect.width - indicatorRect.width),
      tolerance,
      isPositionCorrect
    });
  }

  // Проверяем цвет текста активного элемента
  const labelEl = activeElement.querySelector('.adv-menu__item-label');
  let isColorCorrect = true;

  if (labelEl) {
    const computedStyle = window.getComputedStyle(labelEl);
    const currentColor = computedStyle.color;

    // Парсим RGB цвет
    const rgbMatch = currentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);

      // Проверяем что цвет достаточно светлый (близок к белому)
      // Учитываем что во время transition цвет может быть промежуточным
      // Белый: rgb(255, 255, 255)
      // Допускаем небольшое отклонение для transition state (например, rgb(240, 240, 240))
      const colorThreshold = 200; // Минимальное значение для каждого канала

      isColorCorrect = r >= colorThreshold && g >= colorThreshold && b >= colorThreshold;

      if (props.debugStateVerification) {
        console.log('[MenuVerification] Color check', {
          currentColor,
          rgb: { r, g, b },
          colorThreshold,
          isColorCorrect
        });
      }
    }
  } else if (props.debugStateVerification) {
    console.log('[MenuVerification] Label element not found');
  }

  // Если что-то не так - принудительно обновляем
  if (!isPositionCorrect || !isColorCorrect) {
    // Проверяем не пытаемся ли мы исправить ту же проблему повторно
    const now = Date.now();
    const isSameProblem = lastVerificationState &&
      lastVerificationState.activeId === activeId &&
      lastVerificationState.isPositionCorrect === isPositionCorrect &&
      lastVerificationState.isColorCorrect === isColorCorrect;
    
    const isInCooldown = lastVerificationState &&
      (now - lastVerificationState.timestamp) < VERIFICATION_COOLDOWN;

    if (isSameProblem && isInCooldown) {
      if (props.debugStateVerification) {
        console.log('[MenuVerification] Skipping sync - same problem detected within cooldown period', {
          timeSinceLastSync: now - lastVerificationState.timestamp,
          cooldown: VERIFICATION_COOLDOWN
        });
      }
      return;
    }

    console.warn('[MegaMenu] State mismatch detected, forcing sync', {
      activeId,
      isPositionCorrect,
      isColorCorrect,
      itemRect: { left: itemRect.left, width: itemRect.width },
      indicatorRect: { left: indicatorRect.left, width: indicatorRect.width }
    });

    // Сохраняем текущее состояние
    lastVerificationState = {
      activeId,
      isPositionCorrect,
      isColorCorrect,
      timestamp: now
    };

    // Принудительная синхронизация БЕЗ debounce
    measurementCache?.invalidate();
    updateIndicator(); // Сразу, без задержки
  } else {
    // Состояние корректно - сбрасываем историю
    lastVerificationState = null;
    
    if (props.debugStateVerification) {
      console.log('[MenuVerification] ✅ State is correct, no sync needed');
    }
  }

  // Проверяем видимость активного пункта (только один раз после монтирования)
  if (!scrollVerificationDone.value) {
    if (props.debugStateVerification) {
      console.log('[MenuVerification] Running one-time scroll visibility check');
    }

    verifyActiveItemVisibility();
    scrollVerificationDone.value = true;
  }
};

// Инициализация verification с MutationObserver
const menuVerification = useMenuStateVerification({
  verificationDelay: props.stateVerificationDelay,
  observeAttributes: true,
  observeChildList: false,
  observeSubtree: true,
  debug: props.debugStateVerification
});

// Wrapper for indicator update that calls composable
const updateIndicator = async () => {
  if (process.env.SERVER || isEffectivelyHidden.value) {
    return;
  }

  await updateIndicatorCore(
      internalActiveId.value,
      processedItems.value,
      menuRef.value,
      sliderRef.value,
      null, // No more button in slider mode
      itemRefs.value
  );

  // Update text colors if enabled
  if (props.enableTextColorTransition && indicatorVisible.value && internalActiveId.value !== null && measurementCache) {
    const activeItemMeasurement = measurementCache.getItemMeasurement(internalActiveId.value);
    if (activeItemMeasurement) {
      textColorManager?.updateTextColors({
        left: activeItemMeasurement.left,
        width: activeItemMeasurement.width
      });
    }
  }
};

// Debounced version for animation triggers
const updateIndicatorDebounced = useDebounceFn(updateIndicator, effectiveTimings.value.indicatorUpdate);

// Resize handling with cancellation support
let pendingResizeCalculation: ReturnType<typeof setTimeout> | null = null;

const updateMenuDebounced = useDebounceFn(() => {
  if (pendingResizeCalculation) {
    clearTimeout(pendingResizeCalculation);
    pendingResizeCalculation = null;
  }

  rafScheduler?.cancelAll();

  pendingResizeCalculation = setTimeout(() => {
    if (optimizationsEnabled.value.rafBatching && rafScheduler) {
      rafScheduler.schedule('resize-recalculation', () => {
        measurementCache?.invalidate();
        invalidateScrollMeasurements();

        rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
        rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');

        pendingResizeCalculation = null;
      }, 'high');
    } else {
      measurementCache?.invalidate();
      invalidateScrollMeasurements();

      updateSliderArrows(sliderRef);
      updateIndicatorDebounced();

      pendingResizeCalculation = null;
    }
  }, 0);
}, effectiveTimings.value.menuResize);

// Wrapper functions for scroll operations
const scrollSlider = (direction: 'left' | 'right') => {
  scrollSliderCore(direction, sliderRef);
};

const scrollToActiveItem = async () => {
  await scrollToActiveItemCore(
      sliderRef,
      menuRef,
      itemRefs,
      internalActiveId,
      props.items
  );
};

// Debounced version for scroll-to-active calls
const scrollToActiveItemDebounced = useDebounceFn(scrollToActiveItem, effectiveTimings.value.scrollToActive);

// Throttled scroll handler for 60 FPS (16ms)
const onSliderScrollThrottled = useThrottleFn(() => {
  if (process.env.SERVER) return;

  try {
    invalidateScrollMeasurements();

    if (optimizationsEnabled.value.rafBatching && rafScheduler) {
      rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
      rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
    } else {
      updateSliderArrows(sliderRef);
      updateIndicatorDebounced();
    }
  } catch (error) {
    if (!process.env.SERVER) {
      console.error('[MegaMenu] Error in scroll handler:', error);
    }
  }
}, effectiveTimings.value.scrollThrottle);

// Debounced final calculations after scroll stops
const onSliderScrollFinalCalculations = useDebounceFn(() => {
  try {
    invalidateScrollMeasurements();
    if (optimizationsEnabled.value.rafBatching && rafScheduler) {
      rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
      rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
    } else {
      updateSliderArrows(sliderRef);
      updateIndicatorDebounced();
    }
  } catch (error) {
    if (!process.env.SERVER) {
      console.error('[MegaMenu] Error in final scroll calculations:', error);
    }
  }
}, effectiveTimings.value.scrollFinalCalculations);

const onSliderScroll = () => {
  onSliderScrollThrottled();
  onSliderScrollFinalCalculations();
};

const onItemClick = (item: ProcessedItem) => {
  if (item.isHidden) return;

  lockScroll();

  internalActiveId.value = item.id;
  
  // Сбрасываем состояние верификации при смене активного пункта
  lastVerificationState = null;
  
  emit("update:modelValue", item.id);
  emit("itemClick", item);

  // Update indicator - use debounced version to allow measurements to complete
  if (optimizationsEnabled.value.rafBatching && rafScheduler) {
    rafScheduler.schedule('scroll-to-active', scrollToActiveItemDebounced, 'normal');
    rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'high');
  } else {
    scrollToActiveItemDebounced();
    updateIndicatorDebounced();
  }
};

watch(() => props.items, () => {
  invalidateProcessedItemsCache();
  measurementCache?.invalidate();
  invalidateScrollMeasurements();
  updateMenuDebounced();
}, {deep: true});

watch(() => props.uniformStyle, () => {
  invalidateProcessedItemsCache();
  if (optimizationsEnabled.value.rafBatching && rafScheduler) {
    rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
    rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
  } else {
    updateSliderArrows(sliderRef);
    updateIndicatorDebounced();
  }
});

watch(
    () => processedItems.value.map(item => ({id: item.id, isHidden: item.isHidden})),
    (newHiddenStates, oldHiddenStates) => {
      if (!oldHiddenStates) return;

      const hasChanges = newHiddenStates.some((newState, index) => {
        const oldState = oldHiddenStates[index];
        return oldState && newState.isHidden !== oldState.isHidden;
      });

      if (hasChanges) {
        measurementCache?.invalidate();
        invalidateScrollMeasurements();

        if (optimizationsEnabled.value.rafBatching && rafScheduler) {
          rafScheduler.schedule('update-indicator', updateIndicatorDebounced, 'normal');
          rafScheduler.schedule('update-slider-arrows', () => updateSliderArrows(sliderRef), 'normal');
        } else {
          updateIndicatorDebounced();
          updateSliderArrows(sliderRef);
        }
      }
    },
    {deep: true}
);

watch(() => props.isHidden, (isHidden) => {
  if (isHidden) {
    rafScheduler?.cancelAll();
    indicatorVisible.value = false;
  } else {
    if (optimizationsEnabled.value.rafBatching && rafScheduler) {
      rafScheduler.schedule('resume-update', () => {
        updateSliderArrows(sliderRef);
        updateIndicatorDebounced();
        rafScheduler.schedule('scroll-to-active', scrollToActiveItemDebounced, 'normal');
      }, 'high');
    } else {
      updateSliderArrows(sliderRef);
      updateIndicatorDebounced();
      scrollToActiveItemDebounced();
    }
  }
});

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
  if (newValue !== undefined && newValue !== internalActiveId.value) {
    // Сбрасываем состояние верификации при внешнем изменении activeId
    lastVerificationState = null;
    
    if (props.externalUpdateMode === 'throttled') {
      setActiveThrottled(newValue);
    } else {
      setActiveDebounced(newValue);
    }
  }

  // Если modelValue стал null - прокручиваем меню к началу
  if ((newValue === null || newValue === '') && sliderRef.value) {
    sliderRef.value.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  }
});

// Lifecycle
onMounted(() => {
  if (process.env.SERVER || props.isHidden) {
    return;
  }

  try {
    if (menuRef.value && optimizationsEnabled.value.visibilityOptimization) {
      visibilityManager?.observe(menuRef.value);
    }

    if (sliderRef.value) {
      sliderRef.value.addEventListener('scroll', onSliderScroll, {passive: true});
    }

    // Запустить наблюдение за состоянием меню (если включено)
    if (menuRef.value && props.enableStateVerification) {
      menuVerification.observe(menuRef.value, verifyIndicatorState);
    }

    if (optimizationsEnabled.value.rafBatching && rafScheduler) {
      rafScheduler.schedule('initial-update', () => {
        updateSliderArrows(sliderRef);
        updateIndicatorDebounced();
        rafScheduler.schedule('scroll-to-active', scrollToActiveItemDebounced, 'normal');
      }, 'high');
    } else {
      updateSliderArrows(sliderRef);
      updateIndicatorDebounced();
      scrollToActiveItemDebounced();
    }
  } catch (error) {
    if (!process.env.SERVER) {
      console.error('[MegaMenu] Error in onMounted:', error);
    }
  }
});

onUnmounted(() => {
  try {
    if (sliderRef.value) {
      sliderRef.value.removeEventListener('scroll', onSliderScroll);
    }

    visibilityManager?.cleanup();
    cancelActiveDebounce();
    cancelActiveThrottle();
    textColorManager?.cleanup();
    willChangeManager?.clearAll();
    cleanupScroll();
    menuVerification.cleanup();

    if (pendingResizeCalculation) {
      clearTimeout(pendingResizeCalculation);
      pendingResizeCalculation = null;
    }

    rafScheduler?.cancelAll();
    measurementCache?.invalidate();
    itemRefs.value = {};
  } catch (error) {
    if (!process.env.SERVER) {
      console.error('[MegaMenu] Cleanup error:', error);
    }
  }
});
</script>

<style lang="scss" scoped>
.adv-menu {
  position: relative;
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
  width: 100%;
  contain: layout style;

  &.--hidden {
    * {
      animation: none !important;
      transition: none !important;
    }

    :deep(.adv-menu__indicator) {
      display: none;
    }
  }
}

// Slider mode styles
.adv-menu__slider {
  position: relative;
  display: flex;
  overflow-x: auto;
  flex-wrap: nowrap;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  --menu-gap: var(--adv-menu-gap, 10px);
  contain: layout style;

  &::-webkit-scrollbar {
    display: none;
  }
}

.adv-menu__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  isolation: isolate;
  backface-visibility: hidden;

  // Hover эффект только для десктопа
  @media (min-width: $breakpoint-lg-min) {
    &:hover {
      background: #fff;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
    }
  }

  &--left {
    left: -4px;
  }

  &--right {
    right: -4px;
  }

  @media (max-width: $breakpoint-md-max) {
    // В мобильном режиме стрелки с градиентным fade-эффектом
    width: 23px;
    height: 100%;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    pointer-events: auto;

    // Убираем круглый фон, оставляем только иконку
    .q-icon {
      background: transparent;
      border-radius: 0;
      padding: 0;
      box-shadow: none;
      font-size: 20px;
      color: rgba(0, 0, 0, 0.6);
    }

    &:hover {
      box-shadow: none;

      .q-icon {
        color: rgba(0, 0, 0, 0.8);
      }
    }

    // Градиент для левой стрелки - плотный у края экрана, размытие внутрь
    &--left {
      left: 0;
      justify-content: flex-end;
      padding-right: 2px;
      background: linear-gradient(to right,
          rgba(255, 255, 255, 1) 0%,
          rgba(255, 255, 255, 1) 30%,
          rgba(255, 255, 255, 0.6) 70%,
          transparent 100%
      );
    }

    // Градиент для правой стрелки - плотный у края экрана, размытие внутрь
    &--right {
      right: 0;
      justify-content: flex-start;
      padding-left: 2px;
      background: linear-gradient(to left,
          rgba(255, 255, 255, 1) 0%,
          rgba(255, 255, 255, 1) 30%,
          rgba(255, 255, 255, 0.6) 70%,
          transparent 100%
      );
    }
  }
}

// Динамические отступы для стрелок (только на десктопе)
@media (min-width: $breakpoint-lg-min) {
  .adv-menu.--has-left-arrow {
    padding-left: 36px;
  }

  .adv-menu.--has-right-arrow {
    padding-right: 36px;
  }
}

// На мобильных устройствах отступы не нужны (стрелки прозрачные)
@media (max-width: $breakpoint-md-max) {
  .adv-menu {
    padding: 0;
  }
}

// Items должны быть выше индикатора
.adv-menu {
  :deep(.adv-menu__item) {
    z-index: 2;
  }
}

// Отключение transition при reduceAnimations
.adv-menu.--reduce-animations {
  :deep(.adv-menu__item-label),
  :deep(.adv-menu__item-icon) {
    transition: none;
    will-change: auto;
  }
}

// Отключение анимации при reduceAnimations
.adv-menu.--reduce-animations {
  :deep(.adv-menu__item.--text-blink) {
    .adv-menu__item-label {
      animation: none;
      text-shadow: 0 0 2px #fff,
      0 0 5px #fff,
      0 0 10px currentColor;
    }

    .adv-menu__item-icon {
      animation: none;
      filter: drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px #fff) drop-shadow(0 0 10px currentColor);
    }
  }
}

// Отключение анимаций фона при reduceAnimations
.adv-menu.--reduce-animations {
  :deep(.adv-menu__item-bg) {
    animation: none !important;
  }
}
</style>
